// PayOrderEditBase.tsx
import { Button, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Switch, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import WebEditUniversal, { WebEditUniversalProps } from '../system/WebEditUniversal';
import getList from '../../utilsTsx/universal/getList';
import { LogoutOutlined } from '@ant-design/icons';
import AntdNotification from '../../utils/extend/AntdNotification';
import fetchApi from '../../utilsTsx/components/system/fetchApi';

export interface PayOrderEditBaseConfig {
    /** 业务 apiId，例如 'payOrder' / 'payOrderBak' */
    apiId: string;
    /** 透传的外部 props（原函数组件 props） */
    outerProps: any;
}

const PayOrderEditBase = (config: PayOrderEditBaseConfig) => {
    const { apiId, outerProps } = config;
    const { defaultService } = outerProps;

    const [form] = Form.useForm();
    const ref = useRef<any>(undefined);
    const [merchantData, setMerchantData] = useState<any>();
    const [merchantOrderTypeData, setMerchantOrderTypeData] = useState<any>();
    const [shopData, setShopData] = useState<any>();
    const [payModeData, setPayModeData] = useState<any>();
    const [payProcessData, setPayProcessData] = useState<any>();
    const [refundProcessData, setRefundProcess] = useState<any>();

    useEffect(() => {
        const fetchData = async () => {
            fetchApi.GET(defaultService, {
                query: {
                    apiId: 'generalData',
                    apiVariable: 'payMode@payProcess@refundProcess',
                    apiExtend: 'showLists'
                }
            }).then(res => {
                if (res.success) {
                    const { payMode, payProcess, refundProcess } = res.data;
                    setPayModeData(payMode);
                    setPayProcessData(payProcess);
                    setRefundProcess(refundProcess);
                }
            });
            fetchApi.GET(defaultService, {
                query: { apiId: 'merchant', apiExtend: 'showList' }
            }).then(res => {
                if (res.success) setMerchantData(res.data);
            });
            fetchApi.GET(defaultService, {
                query: { apiId: 'merchantOrderType', apiExtend: 'showList' }
            }).then(res => {
                if (res.success) setMerchantOrderTypeData(res.data);
            });
            fetchApi.GET(defaultService, {
                query: { apiId: 'shop', apiExtend: 'showList' }
            }).then(res => {
                if (res.success) setShopData(res.data);
            });
        };
        fetchData();
    }, []);

    let extendProps: WebEditUniversalProps = {
        ...outerProps,
        apiId,                               // ← 变量替换硬编码
        serviceId: 'pay',
        dateOptions: {
            columnNames: ['payDate', 'lastPayTime', 'receivedTime']
        },
        displayOptions: {
            width: 900
        },
        formOptions: {
            instance: form,
            children: (
                <Row gutter={[8, 0]}>
                    <Col span={7}>
                        <Form.Item label="id" name="id">
                            <Input disabled={true} />
                        </Form.Item>
                    </Col>

                    <Col span={7}>
                        <Form.Item
                            label="方式"
                            name="payModeId"
                            rules={[{ required: true, message: '支付方式!' }]}>
                            <Select
                                showSearch
                                placeholder="支付方式"
                                optionFilterProp="children"
                                filterOption={(input, option: any) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                                {getList.general(payModeData)}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={5}>
                        <Form.Item
                            label="金额"
                            name="payAmount"
                            rules={[{ required: true, message: '请输入支付金额!' }]}>
                            <InputNumber style={{ width: 120 }} />
                        </Form.Item>
                    </Col>

                    <Col span={5}>
                        <Form.Item label="未退款" name="unreimbursedAmount">
                            <InputNumber style={{ width: 120 }} />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            label="进程"
                            name="payProcessId"
                            rules={[{ required: true, message: '支付进程!' }]}>
                            <Select
                                showSearch
                                placeholder="支付进程"
                                optionFilterProp="children"
                                filterOption={(input, option: any) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                                {getList.general(payProcessData)}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item label="支付时间" name="payDate">
                            <DatePicker format="YYYY-MM-DD HH:mm:ss" />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            label="退款进程"
                            name="refundProcessId"
                            rules={[{ required: true, message: '退款进程!' }]}>
                            <Select
                                showSearch
                                placeholder="退款进程"
                                optionFilterProp="children"
                                filterOption={(input, option: any) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                                {getList.general(refundProcessData)}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item label="支付截止时间" name="lastPayTime">
                            <DatePicker format="YYYY-MM-DD HH:mm:ss" />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            label="商户"
                            name="merchantId"
                            rules={[{ required: true, message: '请选择商户!' }]}>
                            <Select
                                showSearch
                                placeholder="商户"
                                optionFilterProp="children"
                                filterOption={(input, option: any) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                                {getList.general(merchantData, { itemName: 'merchantName' })}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            label="商户单类型"
                            name="merchantOrderTypeId"
                            rules={[{ required: true, message: '请选择商户单类型!' }]}>
                            <Select
                                showSearch
                                placeholder="商户单类型"
                                optionFilterProp="children"
                                filterOption={(input, option: any) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                                {getList.general(merchantOrderTypeData, { itemName: 'orderTypeName' })}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            label="商户单号"
                            name="merchantOrderId"
                            rules={[{ required: true, message: '请输入商户单号!' }]}>
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item label="店铺" name="shopId">
                            <Select
                                showSearch
                                placeholder="店铺"
                                optionFilterProp="children"
                                filterOption={(input, option: any) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                                {getList.general(shopData, {
                                    itemName: 'shopName',
                                    includeNull: true
                                })}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            label="接收时间"
                            name="receivedTime"
                            tooltip='向[返回地址]发送信息后的接收时间'>
                            <DatePicker format="YYYY-MM-DD HH:mm:ss" />
                        </Form.Item>
                    </Col>

                    <Col span={20}>
                        <Form.Item
                            label="返回地址"
                            name="returnedAddress"
                            tooltip='以POST方式回传数据（数据在apiData），http://baseUrl/apiId/apiExtend，例如：...'>
                            <Space.Compact>
                                <Tooltip placement="topLeft" title={form.getFieldValue('returnedAddress')}>
                                    <Input
                                        value={Form.useWatch('returnedAddress', form)}
                                        onChange={(e) => form.setFieldsValue({ returnedAddress: e.target.value })}
                                        style={{ width: 575 }}
                                    />
                                </Tooltip>
                                <Tooltip placement="topLeft" title='执行返回'>
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<LogoutOutlined />}
                                        onClick={() => {
                                            fetchApi.POST(defaultService, {
                                                body: {
                                                    apiId,                       // ← 变量替换硬编码
                                                    apiExtend: 'returnedDataValidation',
                                                    apiData: {
                                                        payOrderId: form.getFieldValue('id')
                                                    }
                                                }
                                            }).then(res => {
                                                if (res?.success === true) {
                                                    AntdNotification('success', '返回成功');
                                                } else {
                                                    AntdNotification('error', res?.message);
                                                }
                                            });
                                        }}
                                    />
                                </Tooltip>
                            </Space.Compact>
                        </Form.Item>
                    </Col>

                    <Col span={4}>
                        <Form.Item
                            label="接收"
                            name="isReceived"
                            valuePropName="checked"
                            initialValue={false}
                            tooltip='向[返回地址]发送信息后的接收状态'>
                            <Switch checked={false} checkedChildren="是" unCheckedChildren="否" />
                        </Form.Item>
                    </Col>

                    <Col span={17}>
                        <Form.Item label="商品详情" name="commodityDetails">
                            <Tooltip placement="topLeft" title={form.getFieldValue('commodityDetails')}>
                                <Input
                                    value={Form.useWatch('commodityDetails', form)}
                                    onChange={(e) => form.setFieldsValue({ commodityDetails: e.target.value })}
                                />
                            </Tooltip>
                        </Form.Item>
                    </Col>

                    <Col span={4}>
                        <Form.Item
                            label="支付成功"
                            name="isPaySuccess"
                            valuePropName="checked"
                            initialValue={false}>
                            <Switch checked={false} checkedChildren="是" unCheckedChildren="否" />
                        </Form.Item>
                    </Col>

                    <Col span={3}>
                        <Form.Item
                            label="状态"
                            name="isUse"
                            valuePropName="checked"
                            initialValue={true}>
                            <Switch checked={true} checkedChildren="启用" unCheckedChildren="关闭" />
                        </Form.Item>
                    </Col>

                    <Col span={9}>
                        <Form.Item
                            label="末次支付单号"
                            name="lastPayOrderId"
                            tooltip='最后一次支付的单号'>
                            <Input disabled={true} />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            label="末次支付金额"
                            name="lastPayAmount"
                            tooltip='最后一次支付的金额'>
                            <InputNumber disabled={true} />
                        </Form.Item>
                    </Col>
                </Row>
            )
        }
    };

    return <WebEditUniversal ref={ref} {...extendProps} />;
};

export default PayOrderEditBase;